// Claims-time benefit caps for OMK Last Expense (BRD §5.7 "Benefit Limitations
// (Retail)"), plus the split-payout and waiting-period rules that go with them.
//
// These are ADJUDICATION caps, not onboarding limits: BRD §10.0 rule 3 —
// "Claims-time benefit caps … the cap is enforced at claim adjudication even if
// the member purchased a higher tier". None of them were enforced in ASPin, so a
// child of 8 and a member with a declared condition in their first year of cover
// were both payable the full tier (OMK individual retest, items 17 and 18).
//
// Kept in lib/ rather than a partner app because the customer journey applies the
// same rules; the two must not drift.
//
// No optional chaining / ES6+ syntax the project's Babel config rejects.

import { policyDetailSources } from './policyDetails.es6';
import {
  getScheduleEntryMedicalConditions,
  isConditionDeclaredOnPolicy,
  isMedicalConditionDeclaredForScheduleEntry
} from './personDetails.es6';

/** Children aged this or below are capped (Insurance Act Cap 487 s.96). */
export const CHILD_AGE_CAP_YEARS = 10;
export const CHILD_BENEFIT_CAP = 100000;
/** Declared medical condition, first year of cover (BRD §5.8). */
export const DECLARED_CONDITION_CAP = 250000;
/** Above this the payout is split into two tranches. */
export const SPLIT_PAYOUT_THRESHOLD = 250000;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}/;

function parseDate(value) {
  if (typeof value !== 'string' || !ISO_DATE.test(value)) {
    return null;
  }
  const parts = value.substring(0, 10).split('-');
  return {
    y: Number(parts[0]),
    m: Number(parts[1]),
    d: Number(parts[2])
  };
}

/** Whole years between two ISO dates. Null when either is missing/unparseable. */
export function yearsBetween(from, to) {
  const a = parseDate(from);
  if (!a) {
    return null;
  }
  let b = parseDate(to);
  if (!b) {
    const now = new Date();
    b = { y: now.getFullYear(), m: now.getMonth() + 1, d: now.getDate() };
  }
  let years = b.y - a.y;
  if (b.m < a.m || (b.m === a.m && b.d < a.d)) {
    years -= 1;
  }
  return years;
}

function isChild(relationship) {
  return /child|son|daughter|sibling/i.test(String(relationship || ''));
}

/** Was the death accidental? */
export function isAccidentalCause(causeOfDeath) {
  return /accident/i.test(String(causeOfDeath || ''));
}

/**
 * What is payable for one member.
 *
 * Nothing is inferred optimistically: a cap only applies when the data it needs is
 * present, so a missing date of birth never silently reduces a claim.
 *
 * @param {object} input
 *   benefitAmount           the member's registered cover, KES
 *   relationship            relationship to the principal
 *   dateOfBirth             yyyy-mm-dd
 *   dateOfDeath             yyyy-mm-dd — age and cover duration are measured to this
 *   declaredMedicalCondition  whether a condition was declared for this member
 *   coverStartDate          when THIS member's cover began (their own add date for a
 *                           mid-term addition, else the policy start)
 *   causeOfDeath            an accidental death is payable in full despite a declared
 *                           condition; an unknown cause is treated as natural
 * @returns {{payable:number, registered:number, capped:boolean, applied:Array, reason:(string|undefined)}}
 */
export function applyBenefitCaps(input) {
  const opts = input || {};
  const registered = Math.max(0, Number(opts.benefitAmount) || 0);
  const applied = [];

  const ageAtDeath = yearsBetween(opts.dateOfBirth, opts.dateOfDeath);
  if (
    isChild(opts.relationship) &&
    ageAtDeath !== null &&
    ageAtDeath <= CHILD_AGE_CAP_YEARS
  ) {
    applied.push({
      amount: CHILD_BENEFIT_CAP,
      reason:
        'Child aged ' +
        ageAtDeath +
        ' — payout capped at KES ' +
        CHILD_BENEFIT_CAP.toLocaleString() +
        ' (Insurance Act Cap 487 s.96)'
    });
  }

  // An accidental death cannot be attributed to the condition the member declared,
  // so the first-year cap does not apply to it and the registered cover is payable
  // in full — the same reasoning that waives the waiting period for an accident.
  if (opts.declaredMedicalCondition && !isAccidentalCause(opts.causeOfDeath)) {
    const yearsOfCover = yearsBetween(opts.coverStartDate, opts.dateOfDeath);
    // The full benefit is reinstated after the first year, so only year one caps.
    if (yearsOfCover !== null && yearsOfCover < 1) {
      applied.push({
        amount: DECLARED_CONDITION_CAP,
        reason:
          'Declared medical condition within the first year of cover — payout capped at KES ' +
          DECLARED_CONDITION_CAP.toLocaleString()
      });
    }
  }

  // Caps are ceilings, so the lowest governs.
  applied.sort(function (a, b) {
    return a.amount - b.amount;
  });
  const ceiling = applied.length ? applied[0].amount : registered;
  const payable = Math.min(registered, ceiling);

  return {
    payable: payable,
    registered: registered,
    capped: payable < registered,
    applied: applied,
    reason: payable < registered
      ? applied
        .map(function (a) {
          return a.reason;
        })
        .join(' · ')
      : undefined
  };
}

/**
 * Was a medical condition declared for the life being claimed for?
 *
 * OMK does not store this on the dependant. It is captured once on the policy, as
 * `medical_pre_condition` plus a `members_with_preexisting_conditions` list naming
 * who it applies to, and the event quotation a claim is built from carries neither
 * — so a reader that only looks at `dependant.additional_details` finds nothing and
 * every declared life reads as clean. That is why the cap below never fired and why
 * the claim screen said "None declared" for a member OMK had in fact declared.
 *
 * The two outcomes are kept apart on purpose:
 *
 *   `declared`         this member is attributable — a dependant level flag, the
 *                      claim's own capture, or being named in the policy's list of
 *                      members who declared something. Only this drives the cap.
 *   `declaredOnPolicy` the policy says someone declared, but names nobody. It must
 *                      NOT cap, or one unattributed "yes" would underpay every life
 *                      on the policy; it is reported so a screen can say so instead
 *                      of flatly claiming nothing was declared.
 *
 * @param {object} dependant  the life being claimed for
 * @param {object} [policy]   the policy, where the declaration actually lives
 * @param {object} [claimAdditionalDetails] claim_details.additional_details, which
 *                            carries `declared_medical_condition` when the journey
 *                            captured it against the claim itself
 * @returns {{declared:boolean, declaredOnPolicy:boolean, conditions:Array}}
 */
export function declaredConditionForMember(
  dependant,
  policy,
  claimAdditionalDetails
) {
  const member = dependant && typeof dependant === 'object' ? dependant : {};
  const claimAdditional =
    claimAdditionalDetails && typeof claimAdditionalDetails === 'object'
      ? claimAdditionalDetails
      : {};
  const sources = policyDetailSources(policy);
  // The policy level readers key off policy_schedule_entries, so present the
  // dependant in the shape they expect rather than duplicating their matching.
  const entry = { dependant: member };

  return {
    declared:
      !!claimAdditional.declared_medical_condition ||
      isMedicalConditionDeclaredForScheduleEntry(entry, sources),
    declaredOnPolicy: isConditionDeclaredOnPolicy(sources),
    conditions: getScheduleEntryMedicalConditions(entry, sources)
  };
}

/**
 * What one claim can actually pay, read straight off the claim record.
 *
 * Every screen that needs this figure was assembling the same six inputs by hand,
 * and they did not agree: the expenses dialog capped a child's claim to KES 100,000
 * while the documents checklist still read the registered KES 500,000 and demanded
 * a death certificate for a balance that could never exist. Deriving it in one place
 * is what stops the two drifting.
 *
 * @param {object} claim   the claim record (claim_details, member, benefits, event_date)
 * @param {object} [policy] the policy, when the screen has it — only used for the
 *                          member's own cover start and declared condition, both of
 *                          which the claim usually carries too
 * @param {string} [benefitType] a specific benefit; otherwise the largest is used,
 *                          which is the one a payout is assessed against
 * @returns {object} as applyBenefitCaps, plus `benefit` — the benefit it resolved
 */
export function payableForClaim(claim, policy, benefitType) {
  const record = claim && typeof claim === 'object' ? claim : {};
  const claimDetails = record.claim_details || {};
  const claimAdditional = claimDetails.additional_details || {};
  const deceased = (record.member && record.member.dependant) || {};
  const thePolicy = policy && typeof policy === 'object' ? policy : {};
  const benefits = Array.isArray(record.benefits) ? record.benefits : [];

  let benefit = null;
  if (benefitType) {
    for (let i = 0; i < benefits.length; i += 1) {
      if (benefits[i] && benefits[i].type === benefitType) {
        benefit = benefits[i];
        break;
      }
    }
  } else {
    for (let i = 0; i < benefits.length; i += 1) {
      const amount = (benefits[i] && benefits[i].max_benefit_amount_in_cents) || 0;
      if (!benefit || amount > (benefit.max_benefit_amount_in_cents || 0)) {
        benefit = benefits[i];
      }
    }
  }

  const registered = benefit ? (benefit.max_benefit_amount_in_cents || 0) / 100 : 0;
  const condition = declaredConditionForMember(
    deceased,
    thePolicy,
    claimAdditional
  );

  const result = applyBenefitCaps({
    benefitAmount: registered,
    relationship: claimDetails.relationship_to_policyholder || deceased.relationship,
    dateOfBirth: claimDetails.dependant_dob || deceased.date_of_birth,
    dateOfDeath: record.event_date || claimAdditional.date_of_death,
    declaredMedicalCondition: condition.declared,
    causeOfDeath: claimAdditional.cause_of_death,
    coverStartDate:
      (benefit || {}).waiting_period_start_date ||
      claimAdditional.policy_start_date ||
      thePolicy.start_date
  });
  result.benefit = benefit;
  // Carried on the result so the screens that show why an amount is what it is do
  // not have to re-derive the declaration from a different set of fields.
  result.condition = condition;
  return result;
}

/**
 * Whether the waiting period applies to this claim.
 *
 * BRD §5.6: "Accidental death claims bypass waiting period", and §"Cause of
 * Death-Based Waiting Period Waiver": "If Cause of Death = Accident, the waiting
 * period shall be waived entirely, regardless of whether the Date of Death falls
 * within the waiting period." ASPin rendered the red banner straight off the
 * backend's `is_waiting_period`, which is computed from dates alone, so an
 * accidental death was flagged as in-waiting (retest item 16).
 */
export function isWaitingPeriodWaivedByCause(causeOfDeath) {
  return isAccidentalCause(causeOfDeath);
}

/**
 * How a payout above KES 250,000 is released (BRD §5.10.3 and "For claims above
 * KES 250,000"): the first KES 250,000 within 48 hours on the base documents, the
 * balance once the death certificate is in.
 *
 * @param {number} payable            the capped benefit amount
 * @param {boolean} hasDeathCertificate whether the death certificate is attached
 * @returns {{split:boolean, releasableNow:number, heldBack:number, note:(string|undefined)}}
 */
export function splitPayout(payable, hasDeathCertificate) {
  const total = Math.max(0, Number(payable) || 0);
  if (total <= SPLIT_PAYOUT_THRESHOLD) {
    return { split: false, releasableNow: total, heldBack: 0 };
  }
  if (hasDeathCertificate) {
    return {
      split: true,
      releasableNow: total,
      heldBack: 0,
      note:
        'Death certificate received — the full KES ' +
        total.toLocaleString() +
        ' is releasable.'
    };
  }
  return {
    split: true,
    releasableNow: SPLIT_PAYOUT_THRESHOLD,
    heldBack: total - SPLIT_PAYOUT_THRESHOLD,
    note:
      'First KES ' +
      SPLIT_PAYOUT_THRESHOLD.toLocaleString() +
      ' payable within 48 hours; the balance of KES ' +
      (total - SPLIT_PAYOUT_THRESHOLD).toLocaleString() +
      ' is released once the death certificate is submitted.'
  };
}

/**
 * The payout expressed as the invoices that should be raised for it.
 *
 * A payout over the threshold is not one invoice for the whole amount: it is two,
 * the first for KES 250,000 and the second for the balance — so a KES 500,000
 * payout books as 250,000 + 250,000 rather than a single 500,000 line. Booking them
 * separately is what lets the first tranche be paid out on the base documents while
 * the second is tied to the death certificate.
 *
 * The death certificate governs whether the SECOND invoice can be raised at all,
 * not how the total is divided: with no certificate on file only the first tranche
 * is returned, and the balance is raised once the certificate arrives.
 *
 * @param {number} payable            the capped benefit amount, KES
 * @param {boolean} hasDeathCertificate whether the death certificate is attached
 * @returns {Array} one entry per invoice: {amount, label, heldBack}
 */
export function payoutTranches(payable, hasDeathCertificate) {
  const total = Math.max(0, Number(payable) || 0);
  if (total <= SPLIT_PAYOUT_THRESHOLD) {
    return [{ number: 1, amount: total, label: 'Payout', heldBack: false }];
  }

  const balance = total - SPLIT_PAYOUT_THRESHOLD;
  const tranches = [
    {
      number: 1,
      amount: SPLIT_PAYOUT_THRESHOLD,
      label: 'Invoice 1 of 2 — first tranche',
      heldBack: false
    }
  ];

  if (hasDeathCertificate) {
    tranches.push({
      number: 2,
      amount: balance,
      label: 'Invoice 2 of 2 — balance',
      heldBack: false
    });
  } else {
    // Reported so the dialog can say why only one invoice is being raised, rather
    // than silently booking half the claim.
    tranches.push({
      number: 2,
      amount: balance,
      label: 'Invoice 2 of 2 — balance, held pending the death certificate',
      heldBack: true
    });
  }

  return tranches;
}

/**
 * A stable identifier for one tranche of one claim's payout.
 *
 * Derived rather than descriptive, for two reasons. It has to be unique — every
 * split claim would otherwise carry the same value, which reconciles nothing and
 * trips any uniqueness constraint the field's name implies. And because it is
 * deterministic, the same tranche always produces the same reference, so a retry
 * after a partial failure can recognise what is already booked instead of raising
 * it twice.
 */
export function trancheReference(claimGuid, trancheNumber) {
  return String(claimGuid || '') + '-tranche-' + String(trancheNumber);
}

/** The tranches that can actually be booked now — i.e. excluding anything held back. */
export function bookableTranches(payable, hasDeathCertificate) {
  return payoutTranches(payable, hasDeathCertificate).filter(function (tranche) {
    return !tranche.heldBack;
  });
}
